import type { VoidFunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import * as _ from 'lodash';
import { LabeledCheckbox } from '../../../generic/Forms';
import PropagateModal from '../../shared/SettingsPropagateModal';
import { useAddToast } from '../../../../contexts/ToastContext';
import { useAsyncValue } from '../../../../hooks';
import { patch } from '../../../../redux/api';
import type { FailedFetchResponse } from '../../../../helpers/errors';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import type { ProjectPrivacySettings } from '../../../../../shared/settings/project/privacy';

type PropagateGeneralProjectPrivacySettings = Pick<ProjectPrivacySettings, 'defaultProjectPrivacy'>;

type Props = {
  //  If the modal is open or not
  isOpen: boolean;
  //  We need the current state of the settings so that we can
  //  alter the text on the checkboxes so that it makes sense.
  settings: PropagateGeneralProjectPrivacySettings;
  //  Hide the modal
  onHide: () => void;
  // Endpoint for propagating settings
  endpoint: string;
};

//  All the propagate checkboxes are disabled by default
const resetCheckboxState = {
  defaultProjectPrivacy: false,
};

/**
 * Hook for managing the state of the checkboxes and making the PATCH request.
 */
const usePropagateGeneralProjectPrivacySettings = ({ isOpen, onHide, endpoint }: Omit<Props, 'settings'>) => {
  const addToast = useAddToast();
  //  Void here because we don't really care about the stored value, we're just using this to track the status of the PATCH request.
  const asyncValue = useAsyncValue<void>();
  const [checkboxes, setCheckboxes] = useState<{ defaultProjectPrivacy: boolean }>(resetCheckboxState);

  //  Reset the checkboxes when the modal closes.
  //  We always want the user to select which options should propagate,
  //  which is why none should be checked when the user opens the modal
  useEffect(() => {
    setCheckboxes(resetCheckboxState);
  }, [isOpen]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch<Array<keyof PropagateGeneralProjectPrivacySettings>>(
        endpoint,
        _.compact([checkboxes.defaultProjectPrivacy ? 'defaultProjectPrivacy' : null]),
      );
      asyncValue.setResolve();
      //  Show toast and hide the modal after we successfully propagate.
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: check
  }, [addToast, asyncValue, checkboxes, onHide]);

  return {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isSaveButtonDisabled: asyncValue.isLoading || _.isEqual(checkboxes, resetCheckboxState),
  };
};

/**
 * PropagateGeneralProjectPrivacySettingsModal is a modal for displaying the propagation
 * option for defaultProjectPrivacy and making the PATCH request to propagate the settings.
 */
const PropagateGeneralProjectPrivacySettingsModal: VoidFunctionComponent<Props> = ({
  isOpen,
  settings,
  onHide,
  endpoint,
}) => {
  const { errors, isLoading, checkboxes, setCheckboxes, isSaveButtonDisabled, onSavePropagateSettings } =
    usePropagateGeneralProjectPrivacySettings({
      isOpen,
      onHide,
      endpoint,
    });

  return (
    <PropagateModal
      settingsCategory="privacy"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={
          settings.defaultProjectPrivacy === null
            ? 'Unset the project privacy for all projects'
            : `Set default project privacy to ${settings.defaultProjectPrivacy === 'public' ? 'public' : 'private'}`
        }
        isChecked={checkboxes.defaultProjectPrivacy}
        onChange={(_event, isChecked) =>
          setCheckboxes((currentCheckboxes) => ({
            ...currentCheckboxes,
            defaultProjectPrivacy: isChecked,
          }))}
        isRippleDisabled
      />
    </PropagateModal>
  );
};

export default PropagateGeneralProjectPrivacySettingsModal;
